import React from "react";
import { connect } from "react-redux";
import {
  serviceCaller,
  ServiceCallerProps,
  withLoginRedirect,
} from "../../../composition";
import firebase from "firebase/compat/app";
import "firebase/compat/database";
import { fileRequestConsoleUrl } from "../../../../utils/firebaseConsoleUrls";
import { Col, Row, Button, UncontrolledAlert } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import * as commonActions from "../../../../utils/actions/common";
import { ScaleLoader } from "react-spinners";
import Dashboard from "../../dashboard";

import ICharity from "../../../../types/services/ICharity";
import IColumnName from "../../../../types/IColumnName";
import ISortByType from "../../../../types/services/ISortByType";
import { ISortOption } from "../../sortDropdown";
import { IUploadedFiles } from "../../../../types/IUploadProgress";
import { getParsedDate } from "../../../../utils/helpers";
import {
  FileProcessingRequest,
  FileProcessingRequestStatus,
  inFlightFileRequestStatuses,
} from "../types/FileProcessingRequest.type";
import ReactTooltip from "react-tooltip";
import { ContactsProcessingBannerNotice } from "../importProcessingNotice";
import { getReindexStatus } from "../../../../utils/fetch";

interface Props extends ServiceCallerProps {
  currentCharity: ICharity;
  hideSelection: () => void;
}

interface State {
  isLoaded: boolean;
  message: string;
  columnNames: Array<IColumnName>;
  sortBy: ISortByType;
  showDonorReindexNotice: boolean;
  fileRequests: IUploadedFiles[];
  fileRequestsTableKey: string;
}

const sortOptions: ISortOption[] = [
  { key: "name", show: "User", colName: "name" },
  { key: "fileName", show: "File name", colName: "fileName" },
  {
    key: "timeCreatedPretty",
    show: "Date Created",
    colName: "timeCreatedPretty",
  },
  { key: "operationName", show: "Operation name", colName: "operationName" },
  { key: "status", show: "Status", colName: "status" },
];

class importFileHistoricalList extends React.PureComponent<Props, State> {
  fileRequestsRef: firebase.database.Reference | null = null;
  fileRequestsCallback:
    | ((snapshot: firebase.database.DataSnapshot) => void)
    | null = null;
  lastFileRequestsTableKey = "";

  state = {
    isLoaded: false,
    sortBy: sortOptions[2],
    message: null,
    columnNames: [
      { key: "User", isDisabled: false },
      { key: "File name", isDisabled: false },
      { key: "Uploaded date", isDisabled: false },
      { key: "Operation name", isDisabled: false },
      { key: "Status", isDisabled: false },
    ],
    showDonorReindexNotice: false,
    fileRequests: [],
    fileRequestsTableKey: "",
  };

  columnData = [
    "user",
    "fileName",
    "timeCreatedPretty",
    "operationName",
    "status",
  ];

  componentDidMount() {
    this.props.hideSelection();
    this.attachFileRequestsListener(this.props.currentCharity?.id);
    this.refreshReindexBanner();
  }

  componentWillUnmount() {
    this.detachFileRequestsListener();
  }

  attachFileRequestsListener = (charityId?: string) => {
    this.detachFileRequestsListener();
    if (!charityId) {
      this.setState({
        isLoaded: true,
        fileRequests: [],
        fileRequestsTableKey: "",
      });
      return;
    }

    this.fileRequestsRef = firebase.database().ref(`${charityId}/fileRequests`);
    this.fileRequestsCallback = snapshot => {
      const fileRequests = mapFileRequestRows(snapshot.val());
      const fileRequestsTableKey = fileRequests
        .map(row => row.tableKey)
        .join("|");
      this.setState({
        isLoaded: true,
        fileRequests,
        fileRequestsTableKey,
      });
      if (fileRequestsTableKey !== this.lastFileRequestsTableKey) {
        this.lastFileRequestsTableKey = fileRequestsTableKey;
        this.refreshReindexBanner();
      }
    };
    this.fileRequestsRef.on("value", this.fileRequestsCallback);
  };

  detachFileRequestsListener = () => {
    if (this.fileRequestsRef && this.fileRequestsCallback) {
      this.fileRequestsRef.off("value", this.fileRequestsCallback);
    }
    this.fileRequestsRef = null;
    this.fileRequestsCallback = null;
  };

  refreshReindexBanner = async () => {
    const searchUrl = this.props.config?.searchServiceUrl;
    const charityId = this.props.currentCharity?.id;
    if (!searchUrl || !charityId) {
      this.setState({ showDonorReindexNotice: false });
      return;
    }

    const status = await getReindexStatus(searchUrl, charityId);
    this.setState({ showDonorReindexNotice: status?.donors === true });
  };

  componentDidUpdate(prevProps: Props) {
    if (this.props.currentCharity.id !== prevProps.currentCharity.id) {
      this.lastFileRequestsTableKey = "";
      this.setState({ isLoaded: false, fileRequests: [], fileRequestsTableKey: "" });
      this.attachFileRequestsListener(this.props.currentCharity.id);
    }
  }

  uploadFile = () => {
    if (this.hasInFlightFileRequest()) {
      return;
    }
    this.props.navigate("new");
  };

  hasInFlightFileRequest = () =>
    this.state.fileRequests.some(
      (row: IUploadedFiles) =>
        inFlightFileRequestStatuses.indexOf(
          row.statusCode as FileProcessingRequestStatus
        ) !== -1
    );

  openFileRequestFirebase = (row: IUploadedFiles) => {
    const mainProjectId = (firebase.app().options as { projectId?: string })
      .projectId;
    if (!mainProjectId || !row.requestId) {
      return;
    }
    window.open(
      fileRequestConsoleUrl(
        mainProjectId,
        this.props.currentCharity.id,
        row.requestId
      ),
      "_blank",
      "noopener,noreferrer"
    );
  };

  openImportFileDownload = (row: IUploadedFiles) => {
    if (!row.fileUrl) {
      return;
    }
    window.open(row.fileUrl, "_blank", "noopener,noreferrer");
  };

  getExtraColumns = () => {
    if (!this.props.isAdminDebugUi) {
      return [];
    }
    return [
      {
        id: "firebase-file-request",
        hasTooltip: true,
        tooltipText: "Open in Firebase (file request)",
        type: "icon" as const,
        width: 45,
        faIcon: "fa-database",
        className: "customColUsersIcon",
        showFunctions: this.openFileRequestFirebase,
        isDeactivated: (row: IUploadedFiles) => !row.requestId,
        deactivatedClassName: "customColUsersIconDeactivated",
      },
      {
        id: "download-file-request",
        hasTooltip: true,
        tooltipText: "Download import file",
        type: "icon" as const,
        width: 45,
        faIcon: "fa-download",
        className: "customColUsersIcon",
        showFunctions: this.openImportFileDownload,
        isDeactivated: (row: IUploadedFiles) => !row.fileUrl,
        deactivatedClassName: "customColUsersIconDeactivated",
      },
    ];
  };

  getFiles = () => {
    const fileRequests = this.state.fileRequests || [];
    return Promise.resolve({
      result: fileRequests,
      totalAmount: fileRequests.length,
      pageAmount: 1,
      time: Date.now(),
    });
  };

  renderHistorical = (): JSX.Element => {
    return (
      <Dashboard
        key={this.state.fileRequestsTableKey}
        columnNames={this.state.columnNames}
        columnData={this.columnData}
        extraColumns={this.getExtraColumns()}
        selectionUpdate={null}
        checkCol={false}
        noDataText={"No files uploaded"}
        infoCol={false}
        selectAll={false}
        sortAsc={false}
        currentCharity={this.props.currentCharity}
        getResponse={this.getFiles}
        hideSelection={true}
        sortBy={this.state.sortBy.colName}
        searchText={this.state.fileRequestsTableKey}
        showPagination={false}
        preventScrollOnFetch={true}
      />
    );
  };

  render() {
    const { isLoaded } = this.state;
    const importLocked = this.hasInFlightFileRequest();
    const lockTooltipId = "import-historical-upload-locked";
    const uploadButton = (
      <Button
        onClick={this.uploadFile}
        color="primary"
        className={s.primaryButton}
        disabled={importLocked}
      >
        Upload a new file
      </Button>
    );

    return (
      <main className={s.main}>
        {isLoaded ? (
          <div className={s.container}>
            <Row>
              <Col md={10} sm={12}>
                <h2>Previous uploads</h2>
              </Col>

              {
                <Col sm={12} md={2} className={s.topButtonCol}>
                  {importLocked ? (
                    <span data-tip data-for={lockTooltipId}>
                      {uploadButton}
                      <ReactTooltip
                        id={lockTooltipId}
                        type="info"
                        place="bottom"
                        effect="float"
                      >
                        Wait for the current file to finish processing before
                        you upload a new file
                      </ReactTooltip>
                    </span>
                  ) : (
                    uploadButton
                  )}
                </Col>
              }
            </Row>
            {this.state.showDonorReindexNotice && (
              <UncontrolledAlert color="info" className={s.processingNotice}>
                <ContactsProcessingBannerNotice linkClassName={s.processingNoticeLink} />
              </UncontrolledAlert>
            )}
            {this.renderHistorical()}
          </div>
        ) : (
          <div className="d-flex full-width justify-content-center m-t-200 m-b-300">
            <ScaleLoader color={"#1f65b1"} loading />
          </div>
        )}
      </main>
    );
  }
}

const drawStatusIcon = (
  id: string,
  icon: string,
  iconStyle: string,
  tooltip: string
) => {
  const tooltipId = `${id}-tooltip`;

  return (
    <div
      style={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <i
        className={`fa ${icon} ${iconStyle}`}
        data-tip
        data-for={tooltipId}
        style={{ fontSize: "20px" }}
      />
      <ReactTooltip id={tooltipId} type="info" place="bottom" effect="float">
        {tooltip}
      </ReactTooltip>
    </div>
  );
};

const mapStatusToText = (
  requestRef: string,
  request: FileProcessingRequest
) => {
  switch (request.status) {
    case FileProcessingRequestStatus.RECEIVING:
    case FileProcessingRequestStatus.RECEIVED:
      return drawStatusIcon(
        requestRef,
        "fa-file",
        s.iconProgressColor,
        "File Processing"
      );
    case FileProcessingRequestStatus.VALIDATING:
    case FileProcessingRequestStatus.VALIDATED:
      return drawStatusIcon(
        requestRef,
        "fa-spinner",
        s.iconProgressColor,
        "Validating File"
      );
    case FileProcessingRequestStatus.PROCESSING:
      return drawStatusIcon(
        requestRef,
        "fa-spinner",
        s.iconProgressColor,
        "Uploading File"
      );
    case FileProcessingRequestStatus.UPLOADED:
      return drawStatusIcon(
        requestRef,
        "fa-spinner",
        s.iconProgressColor,
        "File Uploaded"
      );
    case FileProcessingRequestStatus.VALIDATING_PHONE_NUMBERS:
      return drawStatusIcon(
        requestRef,
        "fa-spinner",
        s.iconProgressColor,
        "Validating Phone Numbers"
      );
    case FileProcessingRequestStatus.PROCESSED:
      return drawStatusIcon(
        requestRef,
        "fa-check",
        s.iconSuccessColor,
        "File Was Successfully Uploaded"
      );
    case FileProcessingRequestStatus.INVALID_FORMAT:
      return drawStatusIcon(
        requestRef,
        "fa-exclamation-triangle",
        s.iconFormatColor,
        "Invalid file format"
      );
    case FileProcessingRequestStatus.INDEXING_FAILED:
      return drawStatusIcon(
        requestRef,
        "fa-exclamation",
        s.iconErrorColor,
        "Search indexing failed"
      );
    case FileProcessingRequestStatus.FAILED:
      return drawStatusIcon(
        requestRef,
        "fa-exclamation",
        s.iconErrorColor,
        request.error && request.error.message
          ? request.error.message
          : "Upload failed"
      );
    default:
      return drawStatusIcon(
        requestRef,
        "fa-question-circle",
        s.iconProgressColor,
        request.status || "Unknown status"
      );
  }
};

const mapRequestType = (request: FileProcessingRequest) => {
  const requestType = "Contacts upload";
  const metadata = request.metadata;

  if (metadata) {
    const fileType = metadata["fileType"];
    if (fileType) {
      switch (fileType) {
        case "AppendFile":
          return metadata["replaceFileSegments"] === true
            ? `${requestType}: file append, segments replaced`
            : `${requestType}: file append`;
        case "ReplaceFile":
          return `${requestType}: total replacement`;
      }
    }
  }

  return requestType;
};

const mapFileRequestRows = (
  fileRequestsData: Record<string, FileProcessingRequest> | null
): IUploadedFiles[] => {
  if (!fileRequestsData || typeof fileRequestsData !== "object") {
    return [];
  }

  return Object.keys(fileRequestsData)
    .map(requestId => {
      const data = fileRequestsData[requestId];
      if (!data || typeof data !== "object") {
        return null;
      }
      const errorMessage =
        data.error && data.error.message ? data.error.message : "";

      return {
        user: (data.uploadedBy && data.uploadedBy.userName) || "",
        fileName: data.fileName,
        timeCreated: data.uploadedDateTimestamp,
        timeCreatedPretty: getParsedDate(
          new Date(data.uploadedDateTimestamp).toString(),
          false,
          "MM/DD/YYYY, hh:mm a"
        ),
        operationName: mapRequestType(data),
        status: mapStatusToText(requestId, data),
        requestId,
        statusCode: data.status,
        fileUrl: data.fileUrl,
        tableKey: `${requestId}:${data.status}:${errorMessage}`,
      };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        (Number(b.timeCreated) || 0) - (Number(a.timeCreated) || 0)
    );
};

const mapStateToProps = state => ({
  currentCharity: state.user.currentCharity,
  id: state.user.id,
});

const mapDispatchToProps = dispatch => ({
  hideSelection: () => {
    dispatch(commonActions.hideSelection());
  },
});

export default serviceCaller(
  withLoginRedirect(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(withStyles(s)(importFileHistoricalList))
  )
);
